import { SfLog } from '@app/adapters/logger'

const KEY_PREFIX_DELIMITERS = ['/', '-', '.', ':']
const BYTES_PER_UTF16_CHAR = 2
const WRITE_FAILURE_REPORT_COOLDOWN_MS = 60 * 60 * 1000

let lastWriteFailureReportedAt = 0

interface LocalStorageUsage {
  prefix: string
  count: number
  approxBytes: number
}

export function setJSONValue<A>(key: string, value: A): void {
  window.localStorage.setItem(key, JSON.stringify(value))
}

export function setSafeJSONValue<A>(key: string, value: A): void {
  try {
    setJSONValue(key, value)
  } catch (error) {
    reportWriteFailure(key, error)
  }
}

function reportWriteFailure(key: string, error: unknown): void {
  const now = Date.now()

  if (now - lastWriteFailureReportedAt < WRITE_FAILURE_REPORT_COOLDOWN_MS) {
    return
  }
  lastWriteFailureReportedAt = now

  SfLog.warn('Failed to set value in localStorage', {
    error,
    extra: { key, usage: localStorageUsageByPrefix() },
  })
}

export function localStorageUsageByPrefix(): LocalStorageUsage[] {
  const totals: Record<string, { count: number; approxBytes: number }> = {}

  try {
    Object.keys(window.localStorage).forEach((key) => {
      const prefix = keyPrefix(key)
      const value = window.localStorage.getItem(key) ?? ''
      const bucket = totals[prefix] || { count: 0, approxBytes: 0 }

      bucket.count += 1
      bucket.approxBytes += (key.length + value.length) * BYTES_PER_UTF16_CHAR
      totals[prefix] = bucket
    })
  } catch {
    return []
  }

  return Object.entries(totals)
    .map(([prefix, { count, approxBytes }]) => ({ prefix, count, approxBytes }))
    .sort((a, b) => b.approxBytes - a.approxBytes)
}

function keyPrefix(key: string): string {
  const delimiterIndexes = KEY_PREFIX_DELIMITERS.map((d) =>
    key.indexOf(d)
  ).filter((index) => index > 0)

  return delimiterIndexes.length
    ? key.slice(0, Math.min(...delimiterIndexes))
    : key
}

export function getJSONValue<R>(key: string): R | null {
  const item = window.localStorage.getItem(key)

  if (item) {
    return JSON.parse(item) as R
  }

  return null
}

export const getSafeJSONValue = <R>(key: string): R | null => {
  try {
    return getJSONValue(key)
  } catch {
    return null
  }
}

export const removeJSONValue = (key: string): void => {
  window.localStorage.removeItem(key)
}
